import { useState, useRef, useEffect } from "react";
import type { Lead, LeadSource, LeadStage, Priority } from "@/mocks/leadsData";
import {
  sourceBadgeColors,
  stageBadgeColors,
  priorityBadgeColors,
  priorityDotColors,
  getScoreBadgeColors,
} from "@/mocks/leadsData";

type Props = {
  leads: Lead[];
  selectedIds: Set<number>;
  onToggleSelect: (id: number) => void;
  onToggleSelectAll: () => void;
  allSelected: boolean;
  onView: (lead: Lead) => void;
  onEdit: (lead: Lead) => void;
  onDelete: (lead: Lead) => void;
  sortBy: string | null;
  sortDir: "asc" | "desc";
  onSort: (column: string) => void;
};

export default function LeadsTable({
  leads,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  allSelected,
  onView,
  onEdit,
  onDelete,
  sortBy,
  sortDir,
  onSort,
}: Props) {
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const getInitials = (name?: string) =>
    name
      ? name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
      : "U";

  const SortIcon = ({ column }: { column: string }) => {
    if (sortBy !== column) {
      return <i className="ri-arrow-up-down-line text-[10px] ml-1 text-gray-300" />;
    }
    return (
      <i
        className={`text-[10px] ml-1 text-indigo-600 ${
          sortDir === "asc" ? "ri-sort-asc" : "ri-sort-desc"
        }`}
      />
    );
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" ref={menuRef}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="px-4 py-3.5 text-left w-10">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={onToggleSelectAll}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                />
              </th>
              <th
                className="px-4 py-3.5 text-left font-semibold text-gray-600 cursor-pointer select-none"
                onClick={() => onSort("name")}
              >
                <div className="flex items-center">
                  Name / Company <SortIcon column="name" />
                </div>
              </th>
              <th className="px-4 py-3.5 text-left font-semibold text-gray-600">Contact</th>
              <th className="px-4 py-3.5 text-left font-semibold text-gray-600">Source</th>
              <th className="px-4 py-3.5 text-left font-semibold text-gray-600">Stage</th>
              <th
                className="px-4 py-3.5 text-left font-semibold text-gray-600 cursor-pointer select-none"
                onClick={() => onSort("priority")}
              >
                <div className="flex items-center">
                  Priority <SortIcon column="priority" />
                </div>
              </th>
              <th
                className="px-4 py-3.5 text-left font-semibold text-gray-600 cursor-pointer select-none"
                onClick={() => onSort("score")}
              >
                <div className="flex items-center">
                  Score <SortIcon column="score" />
                </div>
              </th>
              <th className="px-4 py-3.5 text-left font-semibold text-gray-600">Assigned</th>
              <th
                className="px-4 py-3.5 text-left font-semibold text-gray-600 hidden xl:table-cell cursor-pointer select-none"
                onClick={() => onSort("last_activity")}
              >
                <div className="flex items-center">
                  Last Activity <SortIcon column="last_activity" />
                </div>
              </th>
              <th className="px-4 py-3.5 text-right font-semibold text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {leads.map((lead) => {
              const isSelected = selectedIds.has(lead.id);
              return (
                <tr
                  key={lead.id}
                  onClick={() => onView(lead)}
                  className={`hover:bg-gray-50/80 transition-colors cursor-pointer ${
                    isSelected ? "bg-indigo-50/50" : ""
                  }`}
                >
                  <td className="px-4 py-3.5" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onToggleSelect(lead.id)}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-700 flex-shrink-0">
                        {getInitials(lead.name)}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">{lead.name}</p>
                        <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                          {lead.company && <span className="text-xs text-gray-500 truncate">{lead.company}</span>}
                          {(lead.projectName || lead.projectId || lead.project_id) && (
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                const pid = lead.projectId || lead.project_id;
                                if (pid) window.location.href = `/projects/${pid}`;
                              }}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors cursor-pointer"
                              title="Linked Project"
                            >
                              <i className="ri-building-line text-[10px]" />
                              {lead.projectName || "Project"}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3.5 whitespace-nowrap">
                    <div className="space-y-0.5">
                      <p className="text-sm text-gray-600">{lead.phone}</p>
                      <p className="text-xs text-gray-400">{lead.email}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3.5 whitespace-nowrap">
                    <span
                      className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${
                        sourceBadgeColors[lead.source] || "bg-indigo-50 text-indigo-700"
                      }`}
                    >
                      {lead.sourceAccount ? `${lead.source} • ${lead.sourceAccount}` : lead.source}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 whitespace-nowrap">
                    <span
                      className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${
                        stageBadgeColors[lead.stage] || "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {lead.stage}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${
                        priorityBadgeColors[lead.priority] || "bg-gray-100 text-gray-700"
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          priorityDotColors[lead.priority] || "bg-gray-400"
                        }`}
                      />
                      {lead.priority}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center justify-center w-10 h-8 rounded-full text-xs font-bold ${getScoreBadgeColors(
                        lead.score || 50
                      )}`}
                    >
                      {lead.score ?? 50}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-[10px] font-bold text-indigo-700 flex-shrink-0">
                        {getInitials(lead.assigned)}
                      </div>
                      <span className="text-sm text-gray-700">{lead.assigned || "Unassigned"}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3.5 whitespace-nowrap text-sm text-gray-500 hidden xl:table-cell">
                    {lead.lastActivity ? new Date(lead.lastActivity).toLocaleDateString() : "-"}
                  </td>
                  <td className="px-4 py-3.5 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => onView(lead)}
                        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-indigo-600 transition-colors"
                        title="View"
                      >
                        <i className="ri-eye-line text-base" />
                      </button>
                      <button
                        onClick={() => onEdit(lead)}
                        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-indigo-600 transition-colors"
                        title="Edit"
                      >
                        <i className="ri-edit-line text-base" />
                      </button>
                      <div className="relative">
                        <button
                          onClick={() => setOpenMenuId(openMenuId === lead.id ? null : lead.id)}
                          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-indigo-600 transition-colors"
                          title="More"
                        >
                          <i className="ri-more-2-fill text-base" />
                        </button>
                        {openMenuId === lead.id && (
                          <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-lg border border-gray-200 shadow-lg py-1 z-20">
                            <button
                              onClick={() => {
                                onEdit(lead);
                                setOpenMenuId(null);
                              }}
                              className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
                            >
                              <i className="ri-edit-line text-gray-400" /> Edit
                            </button>
                            <button
                              onClick={() => {
                                onView(lead);
                                setOpenMenuId(null);
                              }}
                              className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
                            >
                              <i className="ri-arrow-left-right-line text-gray-400" /> Change Stage
                            </button>
                            <hr className="my-1 border-gray-100" />
                            <button
                              onClick={() => {
                                onDelete(lead);
                                setOpenMenuId(null);
                              }}
                              className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors whitespace-nowrap"
                            >
                              <i className="ri-delete-bin-line" /> Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
            {leads.length === 0 && (
              <tr>
                <td colSpan={10} className="px-5 py-16 text-center">
                  <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-user-search-line text-3xl text-gray-300" />
                  </div>
                  <p className="text-base text-gray-500">No leads in this view</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}